import type { ReactNode } from "react";
import { AppSidebar } from "@/components/AppSidebar";
import { MobileTopBar } from "@/components/MobileTopBar";
import { SidebarProvider } from "@/components/ui/sidebar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { HelpCircle } from "lucide-react";

type FaqEntry = { question: string; answer: ReactNode };
type FaqSection = { id: string; title: string; entries: FaqEntry[] };

const faqSections: FaqSection[] = [
  {
    id: "metrics",
    title: "Metrics and definitions",
    entries: [
      {
        question: "What do the five dashboards measure?",
        answer: (
          <div className="space-y-2">
            <p>Each dashboard covers one practice measure, month by month:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li><strong>Utilisation</strong> — chargeable and non-chargeable hours against available hours.</li>
              <li><strong>Recovery</strong> — how much of the value charged to clients was actually billed rather than written off.</li>
              <li><strong>WIP</strong> — work recorded against assignments but not yet invoiced.</li>
              <li><strong>Debtors</strong> — invoiced amounts still outstanding.</li>
              <li><strong>Lockup</strong> — unbilled WIP and unpaid debt combined, as a single cash measure.</li>
            </ul>
          </div>
        ),
      },
      {
        question: "How is Utilisation % calculated?",
        answer: (
          <div className="space-y-2">
            <p>
              Chargeable hours divided by available hours, for the months and filters currently selected. Average charge-out rate is
              chargeable value divided by chargeable hours over the same selection.
            </p>
            <p>
              Planned (forecast) utilisation uses a different denominator — planned chargeable hours divided by planned chargeable plus
              planned non-chargeable hours — because the forecast has no available-hours equivalent. Actual and planned figures are
              therefore comparable in direction, but they are not calculated identically.
            </p>
          </div>
        ),
      },
      {
        question: "How is Recovery Rate calculated?",
        answer:
          "Net billed divided by the value charged, expressed as a percentage. The remainder is written off. Charged value, net billed value and write-off are also charted separately so the movement in the rate can be traced to one or the other.",
      },
      {
        question: "What does WIP (Aged) mean?",
        answer:
          "Work in progress is time and cost recorded against an assignment that has not yet been invoiced. \"Aged\" refers to how long each balance has sat unbilled. The 90+ day share is presented as a proxy for billing risk — the older a balance, the less likely it is to be recovered in full.",
      },
      {
        question: "What is Lockup?",
        answer:
          "Unbilled WIP plus unpaid aged debt — the total cash tied up between doing the work and being paid for it. The Lockup drill-through splits each row into its WIP, Debt and Lockup components so the driver is visible.",
      },
      {
        question: "What are the age bands?",
        answer:
          "0-30, 31-60, 61-90, 91-120, 121-180 and 180+ days. They apply to WIP, Debtors and Lockup, and drive both the banded charts and the Age Band filter.",
      },
      {
        question: "What does the Burnout Risk chart show?",
        answer:
          "The share of the workforce flagged for sustained overload, derived from utilisation, overtime and time-off patterns relative to grade peers. It is a heuristic indicator built from operational data, not a validated measure of burnout, and is best treated as a prompt to look closer rather than a conclusion.",
      },
    ],
  },
  {
    id: "using",
    title: "Filtering, drill-through and export",
    entries: [
      {
        question: "How do I filter the dashboards?",
        answer: (
          <div className="space-y-2">
            <p>
              Every dashboard has a filter bar above the charts: a month range picker plus a set of multi-select filters. Selections combine,
              and Clear all resets them. Filters carry through to the drill-through tables and to anything you export.
            </p>
            <ul className="list-disc space-y-1 pl-5">
              <li><strong>WIP, Debtors, Lockup</strong> — Service Line, Location, Manager, Category, Age Band.</li>
              <li><strong>Recovery</strong> — Manager, Client Partner, Assignment, Service Line, Location.</li>
              <li><strong>Utilisation</strong> — Location, Employee, Employee Category, Service Line.</li>
            </ul>
          </div>
        ),
      },
      {
        question: "How do I filter data for a specific employee?",
        answer:
          "Utilisation is the employee-level dashboard and carries an Employee filter. WIP, Debtors, Lockup and Recovery are organised by client and assignment rather than by person, so the equivalent there is the Manager, Client Partner or Assignment filter.",
      },
      {
        question: "Why is one of my selected filter values marked as unavailable?",
        answer:
          "The value stays selected but returns no data alongside your other active filters — for example a manager with nothing in the chosen age band. Clearing one of the other filters brings it back into range.",
      },
      {
        question: "Can I export the data?",
        answer: (
          <div className="space-y-2">
            <p>
              Yes, from the drill-through pages. Use Export Filter to choose which tables to include, then Export to Excel. Selecting one
              table downloads a single .xlsx workbook; selecting several downloads a .zip containing one workbook per table.
            </p>
            <p>
              Exports reflect the filters active at the time and are subject to the same access permissions as the on-screen figures. The
              charts themselves are not exported as images.
            </p>
          </div>
        ),
      },
      {
        question: "How do I get to the figures behind a chart?",
        answer:
          "Each dashboard links through to its drill-through tables, which carry your current filters across. From there the rows can be sorted, expanded and exported.",
      },
    ],
  },
  {
    id: "data",
    title: "Data and refresh",
    entries: [
      {
        question: "Where does the data come from?",
        answer:
          "Principally the CCH practice management system, along with Xero and Breathe, plus the reference and mapping files administrators upload under Datacore. These are consolidated by scheduled Microsoft Fabric pipelines and served to the dashboards from there.",
      },
      {
        question: "How current are the figures?",
        answer:
          "Figures are reported by calendar month, and a month becomes visible once its scheduled load has completed. Those loads run centrally on a schedule — no action taken in the dashboard pulls fresh data from the source systems. Administrators can review the most recent file loads under Datacore, Data Uploads.",
      },
      {
        question: "A month, client or employee I expected is missing. Why?",
        answer:
          "Usually one of three things: the load covering that period has not run yet, an active filter is excluding it, or it falls outside the data your account is permitted to see. Check the filter bar first, then your Profile page, then raise it with your administrator.",
      },
    ],
  },
  {
    id: "access",
    title: "Access and accounts",
    entries: [
      {
        question: "Who can access this dashboard?",
        answer: (
          <div className="space-y-2">
            <p>Access is role-based, and there are two roles:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong>Standard user</strong> — the five dashboards, their drill-through tables and exports, restricted to the departments,
                office locations and grades granted to that account.
              </li>
              <li>
                <strong>Administrator</strong> — the above, plus Admin for user and permission management and Datacore for data uploads and
                the Xero integration.
              </li>
            </ul>
          </div>
        ),
      },
      {
        question: "How do I check what data I am permitted to see?",
        answer:
          "Your Profile page lists your own department, office and grade, and separately the departments, locations and grades your data access covers. Every chart, table and export is filtered to that scope.",
      },
      {
        question: "Why do my totals differ from a colleague's?",
        answer:
          "Either your access permissions differ, or your filter selections do. Totals are only comparable when both are the same, so confirm both before treating a difference as a data issue.",
      },
      {
        question: "How do I sign in, and what if I forget my password?",
        answer:
          "Sign in with your email address and password, or use Continue with SSO for single sign-on. An account issued with a temporary password is required to set a new one at first sign-in. Use Reset password on the sign-in page; administrators can also reset an account directly from Admin, Users.",
      },
      {
        question: "I need access to data I cannot currently see.",
        answer:
          "Contact your system administrator. Permissions are set per account against departments, office locations and grades, and take effect the next time you sign in.",
      },
    ],
  },
  {
    id: "assistant",
    title: "Prism Ask",
    entries: [
      {
        question: "What is Prism Ask?",
        answer:
          "The chat panel available on each dashboard. It answers questions about the figures on the page, and a specific chart can be pinned to a question to focus it. Conversations are kept, so an earlier thread can be picked up again.",
      },
      {
        question: "Can Prism Ask see data I am not permitted to see?",
        answer:
          "No. Prism Ask queries the same data as the dashboards and applies the same departmental, location and grade permissions as your account, so it cannot return figures that are out of scope for you.",
      },
      {
        question: "Can I rely on the answers Prism Ask gives?",
        answer:
          "It reads the same underlying data as the charts, but its commentary is generated and should be verified against the relevant chart or drill-through table before being used in client, board or other external material.",
      },
    ],
  },
];

const FAQ = () => {
  return (
    <SidebarProvider>
      <div className="min-h-dvh flex w-full bg-background">
        <AppSidebar />

        <div className="flex-1 flex flex-col min-w-0 h-dvh overflow-hidden">
          <MobileTopBar title="FAQ" />
          <main className="flex-1 p-4 sm:p-6 space-y-6 overflow-auto">
            <div className="flex items-start gap-3 mb-6">
              <HelpCircle className="h-7 w-7 shrink-0 text-primary sm:h-8 sm:w-8" />
              <div>
                <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Frequently Asked Questions</h1>
                <p className="mt-1 text-sm text-muted-foreground">
                  How the metrics are defined, how to filter and export them, and what your account is permitted to see.
                </p>
              </div>
            </div>

            {faqSections.map(section => (
              <Card key={section.id}>
                <CardHeader>
                  <CardTitle>{section.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <Accordion type="single" collapsible className="w-full">
                    {section.entries.map((faq, index) => (
                      <AccordionItem key={faq.question} value={`${section.id}-${index}`}>
                        <AccordionTrigger className="text-left">
                          {faq.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground">
                          {faq.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            ))}

            <Card>
              <CardHeader>
                <CardTitle>Need More Help?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  If your question is not answered here, contact your system administrator. Requests for additional data access, corrections
                  to a figure, or a month that has not appeared are all handled there, as they depend on account permissions and the
                  scheduled data loads rather than on settings available in the dashboard.
                </p>
              </CardContent>
            </Card>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default FAQ;
